import { pickT } from "@repo/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./image-form.i18n";
import type { ComponentFormProps } from "./types";

export type ImageConfig = {
  imageData: string;
  imageType: "png" | "svg";
  width: number;
  align: "left" | "center" | "right";
};

type ImageFormProps = ComponentFormProps<ImageConfig>;

export function ImageForm({ config, onChange, setError }: ImageFormProps) {
  const t = useT(T);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size limits
    const maxSize = file.type === "image/svg+xml" ? 100 * 1024 : 500 * 1024;
    if (file.size > maxSize) {
      setError?.(`${t.fileTooLarge} ${maxSize === 100 * 1024 ? "100 KB" : "500 KB"}`);
      return;
    }

    // Read file as base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      const base64Data = base64.split(",")[1]; // Remove data:image/...;base64, prefix
      onChange({
        ...config,
        imageData: base64Data,
        imageType: file.type === "image/svg+xml" ? "svg" : "png",
      });
      setError?.(null);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="image-upload">
          {t.uploadImage} <span className="text-destructive">*</span>
        </Label>
        <Input id="image-upload" type="file" accept=".png,.svg" onChange={handleFileChange} />
        <p className="text-xs text-muted-foreground">{t.supportedFormats}</p>
        {config.imageData && (
          <div className="space-y-2">
            <div className="text-xs text-success">
              {t.imageUploaded} ({config.imageType?.toUpperCase() || "PNG"})
            </div>
            {/* Image preview */}
            <div
              className="border rounded-md p-2 bg-white"
              style={{
                display: "flex",
                justifyContent:
                  config.align === "left"
                    ? "flex-start"
                    : config.align === "right"
                      ? "flex-end"
                      : "center",
              }}
            >
              <img
                src={`data:image/${config.imageType === "svg" ? "svg+xml" : "png"};base64,${config.imageData}`}
                alt={t.previewAlt}
                style={{
                  maxWidth: `${config.width || 80}%`,
                  maxHeight: "200px",
                  objectFit: "contain",
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">{t.previewNote}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="image-width">{t.width}</Label>
          <Input
            id="image-width"
            type="number"
            min="10"
            max="100"
            value={config.width || 80}
            onChange={(e) => onChange({ ...config, width: parseInt(e.target.value, 10) || 80 })}
          />
          <p className="text-xs text-muted-foreground">{t.widthHelp}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="image-align">{t.alignment}</Label>
          <Select
            value={config.align || "center"}
            onValueChange={(value: "left" | "center" | "right") =>
              onChange({ ...config, align: value })
            }
          >
            <SelectTrigger id="image-align">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">{t.left}</SelectItem>
              <SelectItem value="center">{t.center}</SelectItem>
              <SelectItem value="right">{t.right}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export function validateImageConfig(config: ImageConfig, lang = "en_GB"): string | null {
  const t = pickT(T, lang);
  if (!config.imageData) {
    return t.validationError;
  }
  return null;
}

export function getDefaultImageConfig(): ImageConfig {
  return {
    imageData: "",
    imageType: "png",
    width: 80,
    align: "center",
  };
}
